import { gameProfileApi } from '@libs/shared/src/common/api';
import { userYearSeasonApi } from '@libs/shared/src/common/domains/user/api/UserYearSeasonApi/UserYearSeasonApi';
import moment from 'moment';
import Phaser from 'phaser';
import { Colors } from 'src/utils/enums/colors';

import { GameAssets } from '../../utils/enums/assets';
import { BreackpointsX, BreackpointsY } from '../../utils/enums/breackpoints';
import { Scenes } from '../../utils/enums/scenes';
import { SeasonEnum } from '../../utils/enums/season';
import { getLevelValue } from '../../utils/helpers/getLevelValue';
import { isMobile } from '../../utils/helpers/isMobile';
import { isTodayUserBirthday } from '../../utils/helpers/loadGameAssets';
import { getRandomSpawnPos, getRandomStep } from '../../utils/helpers/random';
import { Text } from '../../utils/types/gameObjects';
import { DynamicGroup, GameObjectWithBody, ImageWithDynamicBody } from '../../utils/types/physics';
import { GameScene } from '../classes/GameScene';
import { UI } from '../ui/ui';
import { UIController } from '../ui/uiController';

const POINT_TO_EARN_PIXELS = 300;
const MAX_SCORE = 1100;

const gameStateLevels = {
  level_1: {
    level: 1,
    score: 0,
    velocityY: 300,
  },
  level_2: {
    level: 2,
    score: 50,
    velocityY: 450,
  },
  level_3: {
    level: 3,
    score: 150,
    velocityY: 600,
  },
  level_4: {
    level: 4,
    score: 300,
    velocityY: 700,
  },
};

export class Game extends GameScene {
  player: ImageWithDynamicBody;
  collectables: DynamicGroup;
  foes: DynamicGroup;
  isGetReward = false;

  ui = { level: null, score: null, lives: null, scoreTextObject: null } as {
    level: Text | null;
    score: Text | null;
    lives: Text | null;
    scoreTextObject: Phaser.GameObjects.Text | null;
  };

  gameState = { ...gameStateLevels.level_1 };
  isDead = false;
  usingArrowKeys = false;
  lives = 3;

  constructor() {
    super(Scenes.GAME);
  }

  init(data) {
    this.user = data.user;
  }

  create() {
    document.body.style.cursor = 'none';
    this.isDead = false;
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.addPointer(1);
    this.input.keyboard.on('keydown-ESC', this.handlePauseGame, this);

    this.setupUI();
    this.setupPlayer();
    this.setupFoes();
    this.setupCollectables();
    this.setupCollisions();
  }

  setupUI() {
    UI.images.Background(this);
    UI.icons.HomeIcon(this, { interactive: true }).on('pointerup', () => {
      this.scene.pause(Scenes.GAME);
      this.scene.launch(Scenes.EXIT_GAME, { level: this.gameState.level, user: this.user });
    });

    this.ui.level = UI.text.Level(this, 1);
    this.ui.score = UI.text.GameScore(this);
    this.ui.lives = UI.text.Lives(this, 3);
    UI.icons.CoinIcon(this);
    UI.icons.LifeIcon(this);
  }

  setupPlayer() {
    this.player = UI.images.Player(this);
    this.player.setCollideWorldBounds(true);
  }

  setupFoes() {
    if (!isTodayUserBirthday(this)) {
      this.foes = this.physics.add.group({
        key: GameAssets.FOE,
        repeat: 4,
        setXY: {
          x: 12,
          y: -1600,
          stepX: getRandomStep(12, window.innerWidth, 50),
        },
        setScale: this.scaleGameItems(),
        allowGravity: false,
        velocityY: this.gameState.velocityY,
      });
    } else {
      const age = moment().diff(this.user.user.birthDate, 'years').toString();
      this.foes = this.physics.add.group();

      for (let i = 0; i < 3; i++) {
        const [x, y] = getRandomSpawnPos(this.game.canvas.width, 40);
        const container = this.add.container(x, y);

        const bg = this.add
          .image(0, 0, GameAssets.FOE)
          .setScale(this.scaleGameItems().x)
          .setOrigin(0.5);

        const label = this.add
          .text(0, 0, age, {
            fontSize: `${this.scaleFontSize()}px`,
            color: Colors.BLACK,
            fontFamily: "'Press Start 2P', 'cursive'",
          })
          .setOrigin(0.5);

        container.add([bg, label]);
        this.physics.world.enable(container);

        const body = container.body as Phaser.Physics.Arcade.Body;
        body.setAllowGravity(false);
        body.setVelocityY(this.gameState.velocityY);
        body.setSize(bg.displayWidth, bg.displayHeight);
        body.setOffset(-bg.displayWidth / 2, -bg.displayHeight / 2);

        this.foes.add(container);
      }
    }
  }

  setupCollectables() {
    this.collectables = this.physics.add.group();

    if (this.user.seasons.seasonName === SeasonEnum.COMPANY_BIRTHDAY) {
      const maxAvatars = 6;

      const spawnedAvatars = [];
      const spawnAvatars = () => {
        if (this.collectables.getLength() >= maxAvatars) {
          return;
        }

        const avatarToSpawn =
          this.user.avatars[Math.floor(Math.random() * this.user.avatars.length)];

        if (!avatarToSpawn) return;

        const textureKey = `avatar_${avatarToSpawn}`;
        const image = this.textures.get(textureKey)?.getSourceImage();

        if (!image) {
          spawnedAvatars.push(avatarToSpawn);
          spawnAvatars();
          return;
        }

        const originalWidth = image.width;
        const originalHeight = image.height;

        const scaleX = 58 / originalWidth;
        const scaleY = 70 / originalHeight;

        const avatar = this.collectables
          .create(getRandomStep(12, window.innerWidth, 50), -100, textureKey)
          .setScale(scaleX, scaleY)
          .setVelocityY(this.gameState.velocityY)
          .setGravity(false);

        spawnedAvatars.push(avatarToSpawn);

        avatar.setCollideWorldBounds(false).on('destroy', () => {
          const index = spawnedAvatars.indexOf(avatarToSpawn);
          if (index > -1) {
            spawnedAvatars.splice(index, 1);
          }
        });
      };
      this.time.addEvent({
        delay: 500,
        callback: spawnAvatars,
        loop: true,
      });
    } else {
      this.collectables = this.physics.add.group({
        key: GameAssets.COIN,
        repeat: 7,
        setXY: {
          x: 12,
          y: -1200,
          stepX: getRandomStep(12, window.innerWidth, 50),
        },
        setScale: this.scaleGameItems(),
        allowGravity: false,
        velocityY: this.gameState.velocityY,
      });
    }
  }

  setupCollisions() {
    this.physics.add.overlap(
      this.player,
      this.collectables,
      this.collectCollectable,
      undefined,
      this
    );
    this.physics.add.overlap(this.player, this.foes, this.hitFoe, undefined, this);
    this.physics.add.overlap(this.foes, this.collectables, this.collideItems, undefined, this);
    this.physics.add.overlap(
      this.collectables,
      this.collectables,
      this.collideItems,
      undefined,
      this
    );
    this.physics.add.overlap(this.foes, this.foes, this.collideItems, undefined, this);
  }

  collideItems(_: GameObjectWithBody, collider: GameObjectWithBody) {
    this.spawnItem(collider);
  }

  async collectCollectable(_: GameObjectWithBody, collider: GameObjectWithBody) {
    this.player?.setTexture(GameAssets.PLAYER_HAPPY);
    this.sound.play('coinSound', { volume: 0.4 });
    this.gameState.score++;
    this.ui.score?.setText(`${this.gameState.score}/${getLevelValue(this.gameState.level)}`);
    this.spawnItem(collider);

    this.time.delayedCall(600, async () => {
      if (this.isDead) return;
      this.player?.setTexture(GameAssets.PLAYER_DEFAULT);

      /**
       * TODO: Consider restoring highScore update during gameplay if needed in the future.
       */

      // if (this.gameState.score > this.user.highScore) {
      //   gameProfileApi
      //     .updateUserProfile({
      //       userId: this.user.user.id,
      //       body: { highScore: this.gameState.score },
      //     })
      //     .catch(console.error);
      // }

      if (!this.isGetReward && this.gameState.score >= POINT_TO_EARN_PIXELS) {
        this.isGetReward = true;
        try {
          await userYearSeasonApi.updateUserYearSeason({
            seasonId: this.user.seasons.id,
            userId: this.user.user.id,
          });
        } catch (err) {
          this.isGetReward = false;
          console.error(err);
        }
      }
    });

    if (this.gameState.score > MAX_SCORE) {
      this.scene.start(Scenes.END_GAME, {
        level: this.gameState.level,
        score: this.gameState.score,
        user: this.user,
      });
      this.resetGameState();
    }
  }

  async hitFoe(_: GameObjectWithBody, collider: GameObjectWithBody) {
    document.body.style.cursor = 'auto';
    this.spawnItem(collider);
    this.sound.play('enemySound', { volume: 0.4 });
    this.lives--;

    if (this.lives <= 0) {
      this.isDead = true;
      this.physics.pause();
      this.player?.setTexture(GameAssets.PLAYER_DEAD);

      if (this.gameState.score > this.user.highScore) {
        try {
          await gameProfileApi.updateUserProfile({
            userId: this.user.user.id,
            body: { highScore: this.gameState.score },
          });
          this.user.highScore = this.gameState.score;
        } catch (err) {
          console.error(err);
        }
      }

      this.time.delayedCall(500, () => {
        const currentScore = this.gameState.score;
        this.scene.stop(Scenes.GAME);
        this.scene.start(Scenes.GAME_OVER, { score: currentScore, user: this.user });
        this.sound.pauseAll();
        this.resetGameState();
        this.sound.play('gameOverSound', { volume: 0.6 });
      });
    }
  }

  handlePointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.usingArrowKeys) {
      const clampedX = Phaser.Math.Clamp(pointer.worldX, 0, this.game.canvas.width);
      this.player?.setX(clampedX);
    }
  }

  handlePauseGame() {
    document.body.style.cursor = 'auto';
    this.physics.pause();
    this.scene.pause(Scenes.GAME);
    this.scene.launch(Scenes.PAUSE_GAME);
  }

  update() {
    const w = this.game.canvas.width;
    const velocityX = 200 + w * 0.57;
    const cursors = this.input.keyboard.createCursorKeys();
    this.usingArrowKeys = false;

    let playerVelocityX = 0;

    if (isMobile()) {
      this.input.on('pointermove', (pointer) => {
        if (pointer.isDown && pointer.y > 60) {
          const playerWidth = (this.player?.displayWidth ?? 0) / 2;
          const clampedX = Phaser.Math.Clamp(
            pointer.x,
            playerWidth,
            this.game.canvas.width - playerWidth
          );
          this.player?.setX(clampedX);
        }
      });

      this.input.on('pointerup', () => this.player?.setVelocityX(0));
    } else {
      if (cursors.left.isDown) {
        playerVelocityX = -velocityX;
        this.usingArrowKeys = true;
      } else if (cursors.right.isDown) {
        playerVelocityX = velocityX;
        this.usingArrowKeys = true;
      }
      this.player?.setVelocityX(this.usingArrowKeys ? playerVelocityX : 0);
    }

    this.collectables?.children.iterate((item) => (this.setItemPosAndVelocity(item), true));
    this.foes?.children.iterate((item) => (this.setItemPosAndVelocity(item), true));
    this.transitionToNewLevel();
    this.ui.lives?.setText(this.lives.toString());
  }

  transitionToNewLevel() {
    const nextLevelKey = `level_${this.gameState.level + 1}`;
    if (
      gameStateLevels[nextLevelKey] &&
      this.gameState.score === gameStateLevels[nextLevelKey].score
    ) {
      this.gameState = { ...gameStateLevels[nextLevelKey] };
      this.ui.level?.setText(`L${this.gameState.level}`);
    }
  }

  scaleGameItems() {
    const [w, h] = UIController.widthHeight(this);
    let scale = 1;
    if (w <= BreackpointsX.LAPTOP || h <= BreackpointsY.LG) scale = 0.6;
    if (w <= BreackpointsX.TABLET) scale = 0.4;
    if (w <= BreackpointsX.MOBILE_SM) scale = 0.35;
    return { x: scale, y: scale };
  }

  scaleFontSize() {
    const [w, h] = UIController.widthHeight(this);
    if (w <= BreackpointsX.MOBILE_SM) return 28;
    if (w <= BreackpointsX.TABLET) return 28;
    if (w <= BreackpointsX.LAPTOP || h <= BreackpointsY.LG) return 38;
    return 44;
  }

  spawnItem(item: Phaser.GameObjects.GameObject) {
    const [posX, posY] = getRandomSpawnPos(this.game.canvas.width, 40);
    if ('body' in item && item.body) {
      (item.body as Phaser.Physics.Arcade.Body).reset(posX, posY);
    }
  }

  setItemPosAndVelocity(item: Phaser.GameObjects.GameObject) {
    const [w, h] = UIController.widthHeight(this);
    if ('body' in item && item.body) {
      const body = item.body as Phaser.Physics.Arcade.Body;
      if (body.velocity.y !== this.gameState.velocityY) {
        body.setVelocityY(this.gameState.velocityY);
      }
      const offsetY = Math.floor(Math.random() * (60 - 10 + 1) + 10);
      const upperY = -Math.floor(Math.random() * (1100 - 800 + 1) + 800);
      if (body.y > h + offsetY || body.y <= upperY || body.x > w - 40) {
        this.spawnItem(item);
      }
    }
  }

  resetGameState() {
    this.gameState = { ...gameStateLevels.level_1 };
    this.lives = 3;
  }
}
